import { FJDecoderManager } from '@fj/codec';
import FJUtil from "../util/FJUtil.js";

class FJWaveform {
    constructor(props) {
        this._renderCanvasInfoList = [];
        this._container = props.container;
        this._waveColor = props.waveColor || 'rgba(160, 169, 207, 1)';
        this._backgroundColor = props.backgroundColor || '#f2f3f7';
        this._ratioOfTimeToPixels = props.ratioOfTimeToPixels || 20;
        this._canvasWidth = props.canvasWidth || 3000;
        this._canvasHeight = props.canvasHeight || 42;
        this._renderDuration = 0;
        this._isNeedUpdateCanvasPosition = props.isNeedUpdateCanvasPosition || false;
    }

    get isNeedUpdateCanvasPosition () {
        return this._isNeedUpdateCanvasPosition;
    }

    get renderDuration () {
        return this._renderDuration;
    }

    set renderDuration (renderDuration) {
        this._renderDuration = renderDuration;
    }

    get renderCanvasInfoList () {
        return this._renderCanvasInfoList
    }

    set renderCanvasInfoList (renderCanvasInfoList) {
        this._renderCanvasInfoList = renderCanvasInfoList;
    }

    get container () {
        return this._container;
    }

    set container (container) {
        this._container = container;
    }

    get waveColor () {
        return this._waveColor;
    }

    set waveColor (waveColor) {
        this._waveColor = waveColor;
    }


    get backgroundColor () {
        return this._backgroundColor;
    }

    set backgroundColor (backgroundColor) {
        this._backgroundColor = backgroundColor;
    }


    get ratioOfTimeToPixels () {
        return this._ratioOfTimeToPixels;
    }

    set ratioOfTimeToPixels (ratioOfTimeToPixels) {
        this._ratioOfTimeToPixels = ratioOfTimeToPixels;
    }

    get canvasWidth () {
        return this._canvasWidth;
    }

    set canvasWidth (canvasWidth) {
        this._canvasWidth = canvasWidth;
    }

    get canvasHeight () {
        return this._canvasHeight;
    }

    set canvasHeight (canvasHeight) {
        this._canvasHeight = canvasHeight;
    }

    _addCanvasToRender (isAddToNodeInRealTime = true) {
        const canvas = document.createElement('canvas');
        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;
        canvas.style.position = 'absolute';
        canvas.style.top = 0 + 'px';
        if (!isAddToNodeInRealTime) {
            canvas.style.left = this.renderDuration * this.ratioOfTimeToPixels + 'px';
        }
        this.renderCanvasInfoList.push({
            duration: 0,
            canvas: canvas
        });
        if (isAddToNodeInRealTime) {
            this.container.appendChild(canvas);
        }
    }

    _updateCanvasPosition () {
        const renderWidth = this.renderDuration * this.ratioOfTimeToPixels;
        let preWidth = 0;
        this.renderCanvasInfoList.forEach((item, index) => {
            const { canvas, duration } = item;
            const itemRenderWidth = duration * this.ratioOfTimeToPixels;
            if (index === 0) {
                if (renderWidth > this.container.clientWidth) {
                    canvas.style.transform = `translateX(${preWidth  - renderWidth + this.container.clientWidth}px)`
                }
            } else {
                canvas.style.transform = `translateX(${preWidth  - renderWidth + this.container.clientWidth}px)`
            }
            preWidth = preWidth + itemRenderWidth;
        });
    }

    /**
     * @description 实时绘制波形图（不清除已生成波形图）
     * @param inputBuffer
     * @param isAddToNodeInRealTime
     */
    drawRealTimeWaveform (inputBuffer, isAddToNodeInRealTime = true) {
        const inputData = new Float32Array(inputBuffer.getChannelData(0));
        const currentDuration = inputBuffer.duration;
        this.drawWaveform(inputData, currentDuration, isAddToNodeInRealTime)
    };

    /**
     * @description 实时绘制波形图（不清除已生成波形图）
     * @param tempData  Float32buffer
     * @param isAddToNodeInRealTime
     */
    drawRealTimeWaveformByFloatArray (tempData, isAddToNodeInRealTime = true) {
        const sampleRate = FJUtil.getAudioSampleRate();
        const currentDuration = tempData.length / 2 / sampleRate;
        this.drawWaveform(tempData, currentDuration, isAddToNodeInRealTime);
    };

    drawWaveform (inputData, currentDuration, isAddToNodeInRealTime) {
        if (this.renderCanvasInfoList.length === 0) {
            this._addCanvasToRender(isAddToNodeInRealTime)
        }
        let currentRenderInfo = this.renderCanvasInfoList[this.renderCanvasInfoList.length - 1];
        const width = currentDuration * this.ratioOfTimeToPixels;
        if (currentRenderInfo.duration * this.ratioOfTimeToPixels + width > this.canvasWidth) {
            this._addCanvasToRender(isAddToNodeInRealTime);
            currentRenderInfo = this.renderCanvasInfoList[this.renderCanvasInfoList.length - 1];
        }

        const currentCanvas = currentRenderInfo.canvas;
        const height = this.canvasHeight;
        const canvasCtx = currentCanvas.getContext('2d');
        const yScale = height / 2;
        canvasCtx.fillStyle = this.backgroundColor;
        canvasCtx.lineWidth = 1;
        canvasCtx.strokeStyle = this.waveColor;
        canvasCtx.beginPath();

        const bufferSize = inputData.length;
        // 创建新的缓冲区
        const data = new Float32Array(bufferSize);
        data.set(inputData, 0);
        const xScale = width / data.length;
        for (let i = 0; i < data.length; i+=64) {
            const x = i * xScale + (currentRenderInfo.duration * this.ratioOfTimeToPixels)
            const y = data[i] * yScale + height/2;
            canvasCtx.lineTo(x, y);
        }
        canvasCtx.stroke();
        this.renderDuration = this.renderDuration + currentDuration;
        currentRenderInfo.duration = currentRenderInfo.duration + currentDuration;
        if (this.isNeedUpdateCanvasPosition) {
            this._updateCanvasPosition();
        }
    };

    /**
     * @description 根据数据直接绘制波形图
     * @param canvas
     * @param data
     * @param options
     */
    static drawWave(canvas, data, options = {}) {
        const { waveColor, backgroundColor } = options;
        const width = canvas.width;
        const height = canvas.height;
        const canvasCtx = canvas.getContext('2d');
        const xScale = width / data.length;
        const yScale = height / 2;

        canvasCtx.fillStyle = backgroundColor;
        canvasCtx.fillRect(0, 0, width, height);

        canvasCtx.lineWidth = 1;
        canvasCtx.strokeStyle = waveColor;
        canvasCtx.beginPath();

        for (let i = 0; i < data.length; i++) {
            const x = i * xScale
            const y = data[i] * yScale + height / 2;
            if (i === 0) {
                canvasCtx.moveTo(x, y);
            } else {
                canvasCtx.lineTo(x, y);
            }
        }
        canvasCtx.stroke();
    };

    /**
     * @description 解码音频 h5
     * @param buffer
     * @param callback
     * @param decodeEndCallback
     */
    decodeAudioDataByH5(buffer, callback = () => {}, decodeEndCallback = () => {}) {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const size = 1024 * 1024 * 0.1;
        const num = Math.ceil(buffer.byteLength / size);
        let i = 0;
        const decode = () => {
            audioCtx.decodeAudioData(
                buffer.slice(size * i, size * (i + 1)),
                (audioBuffer) => {
                    callback(audioBuffer)
                    i += 1;
                    if (i < num) {
                        decode();
                    } else {
                        this.renderCanvasInfoList.forEach(item => {
                            this.container.appendChild(item.canvas);
                        });
                        decodeEndCallback();
                    }
                },
            );
        };
        decode();
    };

    /**
     * @description 解码音频数据
     * @param {File|Blob} file - 文件资源
     * @param {object} options
     * @param {number} options.start - 开始时间
     * @param {number} options.end - 结束时间
     * @param {number} options.speed - 速度
     * @param {string} options.chunkPrefix - id 前缀
     * @param {function} options.chunkCallback - 回调，里面的参数是 indexeddb 的存储 id
     */
    static async decodeAudioData (file, options = {}) {
        const { start, end, speed = 1, chunkPrefix, chunkCallback } = options;
        FJDecoderManager.decodeAudio(file,{
            start,
            end,
            speed,
            chunkPrefix,
            samplerate: FJUtil.getAudioSampleRate(),
            fileType: 'audio',
            mode: 'FFMPEG',
        }, {
            decodeCallback: (frame) => {
                const { data } = frame;
                if (data?.chunk) {
                    chunkCallback(data.chunk);
                }
            }
        })
    };

    load (blob, duration, callback = () => {}) {
        FJWaveform.decodeAudioData(blob, {
            // start: 0,
            // end: duration,
            isBackDataDirectly: true,
            chunkCallback: (data) => {
                // console.log(data, duration)
                if (data) {
                    this.drawRealTimeWaveformByFloatArray(data, false);
                } else {
                    this.renderCanvasInfoList.forEach(item => {
                        this.container.appendChild(item.canvas);
                    });
                    callback();
                }
                // console.log('chunkCallback::', data)
            }
        })
    }
}


export default FJWaveform;
